import { useState } from 'react'
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import NotificationsIcon from '@mui/icons-material/NotificationsOutlined'
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined'
import WarningIcon from '@mui/icons-material/WarningAmberOutlined'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from '../api/notifications'
import type { NotificationSeverity } from '../api/types'
import { formatRelative } from '../lib/format'

const severityIcon: Record<NotificationSeverity, typeof InfoIcon> = {
  info: InfoIcon,
  warning: WarningIcon,
  critical: ErrorIcon,
}

const severityColor: Record<NotificationSeverity, string> = {
  info: 'info.main',
  warning: 'warning.main',
  critical: 'error.main',
}

export function NotificationBell() {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  const { data: count } = useUnreadCount()
  // La lista solo se pide cuando el panel está abierto.
  const { data: page, isLoading } = useNotifications(false, 15)
  const markRead = useMarkNotificationRead()
  const markAll = useMarkAllNotificationsRead()

  const unread = count?.total ?? 0
  const critical = count?.by_severity?.critical ?? 0

  return (
    <>
      <Tooltip title={unread === 0 ? 'Sin novedades' : `${unread} sin leer`}>
        <IconButton onClick={(event) => setAnchor(event.currentTarget)} aria-label="Notificaciones">
          <Badge
            badgeContent={unread}
            max={99}
            // Rojo si hay alguna crítica: un intento de fraude no puede
            // parecer lo mismo que un ingreso normal.
            color={critical > 0 ? 'error' : 'primary'}
          >
            <NotificationsIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 400, maxHeight: 480 } } }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}
        >
          <Typography variant="h3">Notificaciones</Typography>
          {unread > 0 && (
            <Button size="small" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              Marcar todas
            </Button>
          )}
        </Stack>
        <Divider />

        {isLoading ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (page?.data.length ?? 0) === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            No hay notificaciones.
          </Typography>
        ) : (
          <List disablePadding>
            {page?.data.map((notification) => {
              const Icon = severityIcon[notification.severity]

              return (
                <ListItemButton
                  key={notification.uuid}
                  onClick={() => !notification.is_read && markRead.mutate(notification.uuid)}
                  sx={{
                    alignItems: 'flex-start',
                    gap: 1.5,
                    py: 1.25,
                    borderLeft: 3,
                    borderColor: notification.is_read
                      ? 'transparent'
                      : severityColor[notification.severity],
                  }}
                >
                  <Icon sx={{ fontSize: 20, mt: 0.25, color: severityColor[notification.severity] }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: notification.is_read ? 400 : 600 }}
                    >
                      {notification.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {notification.body}
                    </Typography>
                    {notification.created_at && (
                      <Typography variant="caption" color="text.disabled">
                        {formatRelative(new Date(notification.created_at))}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              )
            })}
          </List>
        )}
      </Popover>
    </>
  )
}
